"use client"

import React from "react"
import styled, { css } from "styled-components"
import { Link, navigate } from "gatsby"
import colors from "@constants/colors"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import Backdrop from "@components/Backdrop"
import MenuItem, { ButtonContainer, LinkContainer } from "@components/MenuItem"
import MenuSubGroup from "./MenuSubGroup"
import SelectBrand from "./SelectBrand"
import SelectDevice from "./SelectDevice"
import { Headers, ActionLink, BreadcrumbText } from "./common"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { getIn } from "@utils/index"
import { MobileAndTabletOnly } from "@utils/MediaQuery"
import { FilterFunnelProps, MenuDataProps } from "@utils/types"
import { getShopURL, getShopURLForDevice } from "@utils/urls"
import {
  body14Styles,
  body16MainStyles,
  smallcapsBold13Styles,
} from "@constants/typography"

const openProps = `
  opacity: 1;
  transform: translateX(0);
  transition: opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
`
const Container = styled.div<{ isOpen?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: calc(100% - 72px);
  opacity: 0;
  transform: translateX(100%);
  transition: opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  will-change: opacity, transform;
  background-color: ${colors.offWhite};
  overflow: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  cursor: default;
  ${({ isOpen }) => (isOpen ? openProps : "")}

  @media ${breakpoints.tablet} {
    max-width: 64%;
  }
`

const DeviceTypes = styled.div`
  overflow: hidden;

  ${ButtonContainer} {
    color: ${colors.black};

    svg {
      stroke: ${colors.black};
    }
  }

  ${LinkContainer} {
    color: ${colors.black};

    svg {
      stroke: ${colors.black};
    }
  }
`

const RewardsRow = styled.div`
  ${body16MainStyles}
  display: flex;
  justify-content: space-between;
  padding: 14px 10px 14px 20px;
  color: ${colors.mediumGray};
`
const linkStyles = css`
  ${body14Styles}
  margin-left: 25px;
`

const RewardsLink = styled(Link)`
  ${linkStyles}
  font: ${smallcapsBold13Styles};
  color: ${colors.white};
  background: #f26722;
  padding: 3px 7px 3px 7px;
  border-radius: 2px;
`

const isBrowser = () => typeof window !== "undefined"

export interface MenuLinkProps {
  items?: Array<{ link: string; label: string; external?: boolean }>
  link?: string
  label: string
  external?: boolean
}

interface MobileMenuProps {
  isOpen?: boolean
  isLoggedIn?: boolean
  config?: {
    deviceType?: string
    brand?: string
    showShopOnly?: boolean
  }
  filterFunnel: FilterFunnelProps
  globalData: any
  menuData: MenuDataProps
  onLogout?: () => {}
  onRequestClose?: () => void
  rewardsPoints?: number
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  isLoggedIn,
  config,
  filterFunnel,
  globalData,
  menuData,
  onLogout,
  onRequestClose,
  rewardsPoints,
}) => {
  const menuRef: any = React.useRef(null)
  const selectedInitialState = { deviceType: undefined, brand: undefined }
  const [selected, setSelected] = React.useState<{
    deviceType: string | undefined
    brand: string | undefined
  }>(selectedInitialState)

  const clearAll = () => {
    setSelected(selectedInitialState)
  }

  const selectDeviceType = (deviceTypeSlug: string) => {
    setSelected({
      deviceType: deviceTypeSlug,
      brand: undefined,
    })
  }

  const clearDeviceType = () => {
    setSelected(selectedInitialState)
  }

  const selectBrand = (brandSlug: string) => {
    setSelected({
      deviceType: selected.deviceType,
      brand: brandSlug,
    })
  }

  const clearBrand = () => {
    setSelected({
      deviceType: selected.deviceType,
      brand: undefined,
    })
  }

  const [menuSubGroup, setSubGroup] = React.useState<string | undefined>(
    undefined
  )

  const clearSubGroup = () => {
    setSubGroup(undefined)
  }

  React.useEffect(() => {
    if (!isOpen) {
      clearAll()
      clearSubGroup()
    } else if (config && config.deviceType) {
      setSelected({
        deviceType: config.deviceType,
        brand: config.brand,
      })
    }
  }, [isOpen])

  useModalBackgroundClose({
    modalRef: menuRef,
    excludeIds: ["mobileCollectionsFilterButton"],
    isOpen,
    onRequestClose,
  })

  const selectedDeviceTypeMegaMenuLinks =
    globalData?.megaMenuLinks?.filter(
      ({ slug }) => slug === selected.deviceType
    )[0]?.megaMenuLinks || []

  const formatDeviceType = ({ slug, menuLabel }) => {
    const thisFilterFunnel: any = getIn(filterFunnel, `deviceType.${slug}`, {})
    const brands = Object.keys(thisFilterFunnel)
    const brand = brands.length > 0 && thisFilterFunnel[brands[0]]

    if (slug === "urban-armor-gear-power-and-cables") {
      return {
        label: menuLabel,
        link: "/collections/urban-armor-gear-power-and-cables",
        onClick: onRequestClose,
      }
    } else if (brand && brand.devices.length === 1) {
      const shopURL = getShopURLForDevice(brand.devices[0].slug)
      if (isBrowser() && getIn(window, "location.pathname") !== shopURL) {
        return {
          label: menuLabel,
          link: shopURL,
        }
      } else {
        return {
          label: menuLabel,
          onClick: onRequestClose,
        }
      }
    } else if (brand) {
      return {
        label: menuLabel,
        onClick: () => {
          setSelected({
            deviceType: slug,
            brand: brand.brandSlug,
          })
        },
      }
    } else {
      return {
        label: menuLabel,
        onClick: () => {
          selectDeviceType(slug)
        },
      }
    }
  }

  const formatMenuLink = ({ items, link, label, external }) => {
    if (items) {
      return {
        label,
        onClick: () => {
          setSubGroup(label)
        },
      }
    }

    return {
      label,
      link,
      external,
    }
  }

  const menuItems = getIn(globalData, "menuItems", [])
  const menuLinks = getIn(globalData, "menuLinks", [])
  const formattedDeviceTypes = React.useMemo(
    () => menuItems.map(formatDeviceType),
    [config, filterFunnel, globalData]
  )

  const formattedMenuLinks = React.useMemo(
    () => menuLinks.map(formatMenuLink),
    [config, filterFunnel, globalData]
  )

  const handleRewardsOnClick = () => {
    navigate("/rewards")
  }

  return (
    <MobileAndTabletOnly>
      <Backdrop isOpen={isOpen}>
        <Container id="mobileMenu" ref={menuRef} isOpen={isOpen}>
          <Headers>
            <BreadcrumbText>Shop</BreadcrumbText>
            <ActionLink to={getShopURL()}>All</ActionLink>
          </Headers>
          {isLoggedIn && rewardsPoints && (
            <RewardsRow onClick={handleRewardsOnClick}>
              Rewards
              <RewardsLink to="/rewards">{rewardsPoints} Points</RewardsLink>
            </RewardsRow>
          )}
          <DeviceTypes>
            {formattedDeviceTypes.map((props) => (
              <MenuItem key={props.label} {...props} />
            ))}
          </DeviceTypes>
          {(!config || !config.showShopOnly) && (
            <>
              {formattedMenuLinks.map((props) => {
                if ((isLoggedIn && props.label !== "Rewards") || !isLoggedIn)
                  return <MenuItem key={props.label} {...props} />
              })}
              {isLoggedIn && (
                <MenuItem label="Logout" onClick={onLogout} hideIcon />
              )}
              <MenuSubGroup
                menuSubGroup={menuSubGroup}
                menuSubGroups={menuLinks.filter(({ items }) => !!items)}
                onRequestClose={clearSubGroup}
              />
            </>
          )}
          <SelectBrand
            filterFunnel={filterFunnel}
            deviceTypeSlug={selected.deviceType}
            data={menuData}
            onSelectBrand={selectBrand}
            onRequestClose={clearDeviceType}
            megaMenuLinks={selectedDeviceTypeMegaMenuLinks}
          />
          <SelectDevice
            deviceTypeSlug={selected.deviceType}
            brandSlug={selected.brand}
            data={menuData}
            onRequestClose={clearBrand}
          />
        </Container>
      </Backdrop>
    </MobileAndTabletOnly>
  )
}

export default MobileMenu
